import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  HostListener
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChatService } from '../../../../service/chat.service';
import { HighlightTextPipe } from '../../../../pipe/highlight-text.pipe';
import {
  getFilesFromMessage,
  getMessageMeta,
  getTimeAgo,
  isAudio,
  isDocument,
  isGifMessage,
  isImage,
  isURL,
  isVideo,
  isVoiceMessage
} from '../../utils/helpers.utils';

@Component({
  selector: 'app-quick-search',
  standalone: true,
  imports: [CommonModule, FormsModule, HighlightTextPipe],
  templateUrl: './quick-search.component.html',
  styleUrls: ['./quick-search.component.css']
})
export class QuickSearchComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() chatId: string = '';
  @Input() chatType: 'single' | 'group' = 'single';
  @Input() chatTitle: string = '';
  @Input() userId: string = '';
  @Input() userNameMapping: { [key: string]: string } = {};
  @Input() profileImageMapping: { [key: string]: string } = {};
  @Input() isDarkMode: boolean = false;

  @Output() closePanel = new EventEmitter<void>();
  @Output() messageSelected = new EventEmitter<any>();

  @ViewChild('searchInput') searchInputField?: ElementRef<HTMLInputElement>;
  @ViewChild('resultsContainer') resultsContainer?: ElementRef<HTMLDivElement>;

  searchQuery: string = '';
  startDate: string = '';
  endDate: string = '';
  showDateFilter: boolean = false;

  searchResults: any[] = [];
  totalCount: number = 0;
  page: number = 1;
  limit: number = 20;
  isLoading: boolean = false;
  hasMore: boolean = false;
  hasSearched: boolean = false;
  selectedMessageId: string | null = null;
  currentResultIndex: number = -1;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private ongoingSearchSub?: Subscription;

  constructor(
    private chatService: ChatService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.performSearch(true);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatId']) {
      const prev = changes['chatId'].previousValue;
      const curr = changes['chatId'].currentValue;
      if (prev !== curr) {
        this.resetSearch();
      }
    }

    if (changes['chatTitle']) {
      const prev = changes['chatTitle'].previousValue;
      const curr = changes['chatTitle'].currentValue;
      if (prev && curr && prev !== curr) {
        this.resetSearch();
      }
    }

    if (changes['isOpen']) {
      if (changes['isOpen'].currentValue) {
        setTimeout(() => {
          this.searchInputField?.nativeElement?.focus();
        }, 150);
      } else {
        this.resetSearch();
      }
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.ongoingSearchSub?.unsubscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen) {
      event.preventDefault();
      this.onClose();
    }
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    if (this.startDate || this.endDate) {
      this.performSearch(true);
    } else {
      this.resetSearch();
    }
    this.searchInputField?.nativeElement?.focus();
  }

  toggleDateFilter(): void {
    this.showDateFilter = !this.showDateFilter;
  }

  onDateChange(): void {
    if (this.searchQuery.trim() || this.startDate || this.endDate) {
      this.performSearch(true);
    }
  }

  clearDateFilter(): void {
    this.startDate = '';
    this.endDate = '';
    if (this.searchQuery.trim()) {
      this.performSearch(true);
    } else {
      this.resetSearch();
    }
  }

  get isDateFilterActive(): boolean {
    return !!(this.startDate || this.endDate);
  }

  resetSearch(): void {
    if (this.ongoingSearchSub) {
      this.ongoingSearchSub.unsubscribe();
      this.ongoingSearchSub = undefined;
    }
    this.searchQuery = '';
    this.startDate = '';
    this.endDate = '';
    this.showDateFilter = false;
    this.searchResults = [];
    this.totalCount = 0;
    this.page = 1;
    this.hasMore = false;
    this.hasSearched = false;
    this.isLoading = false;
    this.selectedMessageId = null;
    this.currentResultIndex = -1;
    if (this.searchInputField?.nativeElement) {
      this.searchInputField.nativeElement.value = '';
    }
    this.cdRef.detectChanges();
  }

  performSearch(resetPage: boolean = true): void {
    if (!this.chatId) return;

    const trimmedQuery = this.searchQuery.trim();
    if (!trimmedQuery && !this.startDate && !this.endDate) {
      this.resetSearch();
      return;
    }

    if (this.ongoingSearchSub) {
      this.ongoingSearchSub.unsubscribe();
      this.ongoingSearchSub = undefined;
    }

    if (resetPage) {
      this.page = 1;
      this.searchResults = [];
      this.currentResultIndex = -1;
    }

    this.isLoading = true;
    this.hasSearched = true;

    const filters: any = {
      chatIds: [this.chatId],
      chatType: this.chatType,
      page: this.page,
      limit: this.limit
    };

    if (trimmedQuery) {
      filters.search = trimmedQuery;
    }

    if (this.startDate) {
      try {
        const start = new Date(this.startDate);
        start.setHours(0, 0, 0, 0);
        filters.startDate = start.toISOString();
      } catch (e) {
        console.error('Invalid startDate for quick search:', e);
      }
    }

    if (this.endDate) {
      try {
        const end = new Date(this.endDate);
        end.setHours(23, 59, 59, 999);
        filters.endDate = end.toISOString();
      } catch (e) {
        console.error('Invalid endDate for quick search:', e);
      }
    }

    this.ongoingSearchSub = this.chatService.searchChatMessages(filters).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.ongoingSearchSub = undefined;
        const messages =
          res?.data?.results ||
          (Array.isArray(res?.data) ? res.data : (res?.data?.messages || res?.results || []));
        const total =
          res?.data?.totalCount ??
          res?.data?.totalMessages ??
          res?.data?.count ??
          (res?.totalCount ?? messages.length);

        this.totalCount = typeof total === 'number' ? total : messages.length;

        const enriched = messages.map((msg: any) => this.enrichMessage(msg));

        if (this.page === 1) {
          this.searchResults = enriched;
        } else {
          this.searchResults = [...this.searchResults, ...enriched];
        }

        this.hasMore =
          messages.length >= this.limit && this.searchResults.length < this.totalCount;

        if (this.searchResults.length > 0 && this.currentResultIndex === -1) {
          this.currentResultIndex = 0;
        }

        this.cdRef.detectChanges();
      },
      error: (err: any) => {
        console.error('Quick search error:', err);
        this.isLoading = false;
        this.ongoingSearchSub = undefined;
        if (this.page === 1) {
          this.searchResults = [];
          this.totalCount = 0;
        }
        this.cdRef.detectChanges();
      }
    });
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const atBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    if (atBottom && !this.isLoading && this.hasMore) {
      this.page++;
      this.performSearch(false);
    }
  }

  selectMessage(msg: any, index?: number): void {
    const msgId = msg._id || msg.id || msg.messageId;
    this.selectedMessageId = msgId;
    if (typeof index === 'number') {
      this.currentResultIndex = index;
    }
    this.messageSelected.emit(msg);
  }

  navigateResult(direction: 'prev' | 'next'): void {
    if (!this.searchResults.length) return;

    if (direction === 'prev') {
      if (this.currentResultIndex > 0) {
        this.currentResultIndex--;
      } else {
        this.currentResultIndex = this.searchResults.length - 1;
      }
    } else {
      if (this.currentResultIndex < this.searchResults.length - 1) {
        this.currentResultIndex++;
      } else {
        this.currentResultIndex = 0;
      }
    }

    const targetMsg = this.searchResults[this.currentResultIndex];
    if (targetMsg) {
      this.selectMessage(targetMsg, this.currentResultIndex);
      this.scrollResultCardIntoView(this.currentResultIndex);
    }
  }

  private scrollResultCardIntoView(index: number): void {
    setTimeout(() => {
      const container = this.resultsContainer?.nativeElement;
      if (!container) return;
      const cards = container.querySelectorAll('.quick-search-item');
      const targetCard = cards[index] as HTMLElement;
      if (targetCard) {
        targetCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  }

  onClose(): void {
    this.closePanel.emit();
  }

  private enrichMessage(msg: any): any {
    const rawMsg = msg.message || '';
    const files = getFilesFromMessage(msg);
    const meta = getMessageMeta(msg);

    const hasImage = isImage(msg) || (files.length > 0 && files.some((f: any) => isImage(f)));
    const hasVideo = isVideo(msg) || (files.length > 0 && files.some((f: any) => isVideo(f)));
    const hasAudio = isAudio(msg) || isVoiceMessage(msg);
    const hasDoc = isDocument(msg) || (files.length > 0 && files.some((f: any) => isDocument(f)));

    let mediaBadge = '';
    if (hasImage) mediaBadge = 'Photo';
    else if (hasVideo) mediaBadge = 'Video';
    else if (hasAudio) mediaBadge = 'Audio';
    else if (hasDoc) mediaBadge = 'Document';
    else if (isGifMessage(msg)) mediaBadge = 'GIF';

    return {
      ...msg,
      _id: msg._id || msg.id || msg.messageId,
      id: msg.id || msg._id || msg.messageId,
      messageId: msg.messageId || msg._id || msg.id,
      textPreview: rawMsg,
      mediaBadge,
      files,
      meta,
      hasFiles: files.length > 0,
      relativeTime: getTimeAgo(msg.createdAt)
    };
  }

  getSenderName(msg: any): string {
    const senderId = msg.sender_id || msg.senderId || msg.userId;
    if (senderId && senderId === this.userId) {
      return 'You';
    }
    return (
      (senderId && this.userNameMapping[senderId]) ||
      msg.name ||
      msg.chatName ||
      msg.displayName ||
      'User'
    );
  }

  getSenderAvatar(msg: any): string {
    const senderId = msg.sender_id || msg.senderId || msg.userId;
    if (senderId && senderId === this.userId) {
      return (
        this.profileImageMapping[this.userId] ||
        'assets/user-icon.png'
      );
    }
    return (
      (senderId && this.profileImageMapping[senderId]) ||
      msg.profileImg ||
      msg.profileImage ||
      'assets/user-icon.png'
    );
  }

  handleProfileImageError(event: any): void {
    event.target.src = 'assets/user-icon.png';
  }
}
